import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import GuestRankOverlay from "../components/GuestRankOverlay.jsx";
import ProgressBar from "../components/ProgressBar.jsx";
import ResultPanel from "../components/ResultPanel.jsx";
import { fetchAllQuizzes } from "../api.js";
import { useAuth } from "../hooks/useAuth.js";
import { savePendingAttempt } from "../utils/localScoreCache.js";
import { getOrCreateVisitorToken } from "../utils/visitorToken.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";

const formatCategoryLabel = (category) => {
  if (!category) return "General";
  return category
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const QuizPlayer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, authFetch, updateUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [status, setStatus] = useState("loading");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [relatedQuizzes, setRelatedQuizzes] = useState([]);
  const [showRankOverlay, setShowRankOverlay] = useState(false);

  // Key includes the ref username so that navigating from ?ref=alice to
  // ?ref=bob in the same tab issues a fresh token rather than reusing alice's.
  const refUsername = searchParams.get("ref") || "";
  const refTokenKey = refUsername ? `qf_reftok_${slug}_${refUsername}` : null;

  // Holds the AbortController for the in-flight /referral-start/ request so
  // submitQuiz can cancel it if the response arrives after scoring completes.
  const referralAbortRef = useRef(null);

  // Pending auto-advance timer — cancelled before scheduling a new one so
  // rapid taps or choice-switching never queue multiple increments.
  const advanceTimerRef = useRef(null);

  const fetchRelatedQuizzes = useCallback(async () => {
    try {
      const data = await fetchAllQuizzes();
      setRelatedQuizzes(data);
    } catch (err) {
      console.error("Unable to load related quizzes", err);
    }
  }, []);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setStatus("loading");
        const response = await fetch(`${API_BASE}/quizzes/${slug}/`);
        if (response.status === 404) {
          navigate("/", { replace: true });
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to load quiz");
        }
        const data = await response.json();
        setQuiz(data);
        setStatus("success");
      } catch (err) {
        console.error(err);
        setStatus("error");
      }
    };

    fetchQuiz();
  }, [slug, navigate]);

  // When a ?ref=<username> param is present and the quiz has loaded, call
  // /referral-start/ to obtain a server-signed referral token. The token
  // embeds the visitor_token (stable per tab session) so the backend can
  // derive a tamper-proof per-session fingerprint at score time.
  // Stored in sessionStorage so a page refresh doesn't re-trigger the call;
  // cleared after score submission to prevent double-crediting.
  useEffect(() => {
    if (!refUsername || !quiz || !refTokenKey) return;
    // Don't re-fetch if we already have a valid token for this ref+quiz combo.
    if (sessionStorage.getItem(refTokenKey)) return;

    const controller = new AbortController();
    referralAbortRef.current = controller;

    const visitorToken = getOrCreateVisitorToken();
    fetch(`${API_BASE}/quizzes/${quiz.slug}/referral-start/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ref_username: refUsername, visitor_token: visitorToken }),
      signal: controller.signal,
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.referral_token) {
          sessionStorage.setItem(refTokenKey, data.referral_token);
        }
      })
      .catch(() => {}); // AbortError and network errors must never interrupt the quiz

    return () => controller.abort();
  }, [quiz, refUsername, refTokenKey]);

  const currentQuestion = useMemo(() => {
    if (!quiz) return null;
    return quiz.questions[currentIndex];
  }, [quiz, currentIndex]);

  const handleSelect = (questionId, choiceId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: choiceId }));

    const isLast = currentIndex === quiz.questions.length - 1;
    if (!isLast) {
      // Cancel any pending advance before scheduling a new one so rapid taps
      // or choice-switching never queue multiple increments.
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        setCurrentIndex((prev) => Math.min(prev + 1, quiz.questions.length - 1));
      }, 400);
    }
  };

  const nextQuestion = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, quiz.questions.length - 1));
  };

  const previousQuestion = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const restart = () => {
    setAnswers({});
    setCurrentIndex(0);
    setResult(null);
    setError(null);
    setShowRankOverlay(false);
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    setSubmitting(true);
    setError(null);
    // Capture the submitting user's id so the post-response updateUser call
    // can verify the session hasn't changed before writing points to context.
    const submittingUserId = user?.id ?? null;
    try {
      const payload = {
        answers: quiz.questions
          .map((question) => ({
            question_id: question.id,
            choice_id: answers[question.id],
          }))
          .filter((entry) => entry.choice_id),
      };

      // Include the server-signed referral token if one was obtained when
      // the visitor arrived via a share link. Cleared after submission so
      // retakes don't re-credit the referrer.
      const referralToken = refTokenKey ? sessionStorage.getItem(refTokenKey) : null;
      if (referralToken) payload.referral_token = referralToken;
      const response = await authFetch(`${API_BASE}/quizzes/${quiz.slug}/score/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Unable to submit results");
      }
      const data = await response.json();

      // Anonymous flow: cache the signed attempt token for redemption on register
      if (!user && data.attempt_token) {
        savePendingAttempt(quiz.slug, data.attempt_token, data.points_would_earn || 0);
      }

      // Sync the nav bar / auth context with the user's new point total so the
      // header updates immediately without a full page refresh. Pass the id
      // guard so a delayed response never overwrites a different user's session.
      if (submittingUserId != null && data.total_points != null) {
        updateUser({ total_points: data.total_points }, submittingUserId);
      }

      // Abort any in-flight /referral-start/ request before clearing the token.
      // Without this, a slow network response arriving after clearance would
      // repopulate the token, causing the next retake to re-credit the referrer.
      referralAbortRef.current?.abort();
      referralAbortRef.current = null;
      if (refTokenKey) sessionStorage.removeItem(refTokenKey);

      const isPersonality = data.quiz_type === "personality";
      setResult({
        title: data.result?.title || (isPersonality ? "Your result is in!" : data.passed ? "Nice work" : "Close call"),
        body:
          data.result?.body ||
          (isPersonality
            ? "Based on your answers, here's what we found."
            : data.passed
            ? "You cleared the pass bar! Share it and challenge someone."
            : "You were close. Check the answers and try again."),
        isPersonality,
        personalityResultKey: data.personality_result_key,
        stats: {
          score: data.score,
          total: data.total,
          percent: data.percent,
          passed: data.passed,
        },
        breakdown: data.breakdown || [],
        // Auth fields (undefined when anonymous)
        pointsEarned: data.points_earned,
        attemptNumber: data.attempt_number,
        totalPoints: data.total_points,
        badgesEarned: data.badges_earned || [],
        // Anonymous fields
        pointsWouldEarn: data.points_would_earn,
        rank: data.rank,
        percentile: data.percentile,
        tiedWith: data.tied_with,
        rankIsToday: data.rank_is_today,
      });
      // Show the rank overlay for anonymous trivia users only
      if (!user && !isPersonality) setShowRankOverlay(true);
      fetchRelatedQuizzes();
    } catch (err) {
      console.error(err);
      setError("Couldn't submit your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return <div className="h-64 animate-pulse rounded-3xl bg-white/5" />;
  }

  if (status === "error" || !quiz) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-white/70">
        We couldn't load that quiz.
        <div className="mt-4">
          <Link to="/" className="text-brand-glow underline">
            Back to quizzes
          </Link>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="space-y-6">
        {quiz && (
          <Helmet>
            <title>{quiz.title} — Quiz | QuizFreak</title>
            <meta name="description" content={quiz.description || `Take the ${quiz.title} quiz on QuizFreak. Free online quiz.`} />
            <link rel="canonical" href={`https://quizfreak.com/v2/quiz/${slug}/`} />
          </Helmet>
        )}
        {showRankOverlay && (
          <GuestRankOverlay
            result={result}
            onDismiss={() => setShowRankOverlay(false)}
          />
        )}
        <ResultPanel
          quiz={quiz}
          result={result}
          relatedQuizzes={relatedQuizzes}
          onRestart={restart}
        />
        <Link to="/" className="inline-flex items-center text-sm font-semibold text-brand-glow">
          ← More quizzes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>{quiz.title} — Quiz | QuizFreak</title>
        <meta name="description" content={quiz.description || `Take the ${quiz.title} quiz on QuizFreak. Free online quiz.`} />
        <link rel="canonical" href={`https://quizfreak.com/v2/quiz/${slug}/`} />
      </Helmet>
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/5">
        {quiz.image ? (
          <img
            src={quiz.image}
            alt={quiz.title}
            className="h-52 w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-52 w-full items-center justify-center rounded-t-3xl bg-gradient-to-br from-brand-glow/30 to-purple-900/40">
            <span className="text-white/60 text-sm uppercase tracking-[0.4em]">
              {formatCategoryLabel(quiz.category)}
            </span>
          </div>
        )}
        <div className="p-6">
          <p className="text-sm uppercase tracking-[0.3em] text-white/60">Quiz</p>
          <h1 className="mt-2 text-4xl font-semibold text-white">{quiz.title}</h1>
          <p className="mt-1 text-white/70">{quiz.description}</p>
        </div>
      </div>
      <ProgressBar current={currentIndex + 1} total={quiz.questions.length} />
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-sm uppercase tracking-[0.3em] text-white/60">
          Question {currentIndex + 1}
        </p>
        {currentQuestion?.image && (
          // Generic alt text on purpose — many image-based quizzes ask the
          // user to identify the subject (e.g. "Which '90s cartoon character
          // is this?"), so a descriptive alt would spoil the answer.
          <img
            src={currentQuestion.image}
            alt={`Question ${currentIndex + 1}`}
            className="mt-3 max-h-72 w-full rounded-2xl object-contain bg-black/20"
            loading="lazy"
          />
        )}
        <h2 className="mt-2 text-2xl font-semibold text-white">{currentQuestion?.prompt}</h2>
        <div className="mt-4 space-y-3">
          {currentQuestion?.choices.map((choice) => (
            <button
              key={choice.id}
              type="button"
              onClick={() => handleSelect(currentQuestion.id, choice.id)}
              className={`w-full rounded-2xl border px-4 py-3 text-left text-lg font-medium transition
                ${
                  answers[currentQuestion.id] === choice.id
                    ? "border-brand-glow bg-brand-glow/20 text-white"
                    : "border-white/10 bg-white/5 text-white/80 hover:border-white/40"
                }
              `}
            >
              {choice.text}
            </button>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={previousQuestion}
          disabled={currentIndex === 0}
          className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white/70 disabled:opacity-40"
        >
          Previous
        </button>
        {currentIndex === quiz.questions.length - 1 && (
          <button
            type="button"
            onClick={submitQuiz}
            disabled={submitting || !answers[currentQuestion?.id]}
            className="inline-flex items-center gap-2 rounded-full bg-brand-glow px-6 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {submitting ? (quiz.quiz_type === "personality" ? "Revealing..." : "Scoring...") : (quiz.quiz_type === "personality" ? "See my result" : "See my score")}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizPlayer;
